import { useState, useEffect } from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { Link } from "react-router-dom";
import { FEED_QUERY, VOTE } from "../graphql";

interface Post {
  id: string;
  title: string;
  url?: string | null;
  authorId: string;
  score: number;
  commentCount: number;
  createdAt: string;
}

function recentBuckets(n = 72): string[] {
  const buckets: string[] = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getTime() - i * 60 * 60 * 1000);
    buckets.push(d.toISOString().substring(0, 13));
  }
  return buckets;
}

export function Feed() {
  const client = useApolloClient();
  const [items, setItems] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [votedIds, setVotedIds] = useState<Set<string>>(new Set());
  const [vote] = useMutation(VOTE);

  async function loadFeed() {
    setLoading(true);
    setError(null);
    const buckets = recentBuckets(72); // last 3 days
    const all: Post[] = [];

    for (const bucket of buckets) {
      if (all.length >= 30) break;
      try {
        const { data } = await client.query({
          query: FEED_QUERY,
          variables: { limit: 30, bucket },
          fetchPolicy: "network-only",
        });
        const fetched: Post[] = data?.feed?.items ?? [];
        all.push(...fetched);
      } catch (e: any) {
        setError(e.message);
        break;
      }
    }

    // sort by score descending
    all.sort((a, b) => b.score - a.score);
    setItems(all.slice(0, 30));
    setLoading(false);
  }

  useEffect(() => {
    loadFeed();
  }, []);

  if (loading) return <FeedSkeleton />;
  if (error) return <ErrorBanner message={error} />;
  if (items.length === 0) {
    return (
      <div className="card p-10 text-center">
        <h2 className="text-lg mb-2">No posts yet</h2>
        <p className="text-mute mb-4">Be the first to share something.</p>
        <Link to="/submit" className="btn-primary">
          Submit a post →
        </Link>
      </div>
    );
  }

  return (
    <ol className="space-y-2">
      {items.map((p, i) => (
        <li
          key={p.id}
          className="card p-4 hover:border-white/10 transition-colors animate-slide-up"
        >
          <div className="flex items-start gap-4">
            <div className="flex flex-col items-center pt-1 w-10 shrink-0">
              <button
                aria-label={votedIds.has(p.id) ? "unvote" : "upvote"}
                onClick={() =>
                  vote({
                    variables: { targetType: "POST", targetId: p.id },
                  }).then(() => {
                    setVotedIds((prev) => {
                      const next = new Set(prev);
                      next.has(p.id) ? next.delete(p.id) : next.add(p.id);
                      return next;
                    });
                    loadFeed();
                  })
                }
                className={`grid place-items-center h-8 w-8 rounded-lg transition-colors
    ${
      votedIds.has(p.id)
        ? "text-accent bg-accent/10"
        : "text-mute hover:text-accent hover:bg-accent/10"
    }`}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="currentColor"
                >
                  <path d="M12 4l8 10h-5v6h-6v-6H4z" />
                </svg>
              </button>
              <span className="text-sm text-white font-semibold tabular-nums">
                {p.score}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-mute text-sm tabular-nums w-6">
                  {i + 1}.
                </span>
                <Link
                  to={`/post/${p.id}`}
                  className="text-white font-medium hover:text-accent-soft truncate"
                >
                  {p.title}
                </Link>

                {p.url && <span className="chip">{hostOf(p.url)}</span>}
              </div>
              <div className="mt-1 ml-8 text-xs text-mute flex items-center gap-3">
                <span>by {p.authorId.slice(0, 8)}</span>
                <span>·</span>
                <span>{timeAgo(p.createdAt)}</span>
                <span>·</span>
                <Link to={`/post/${p.id}`} className="hover:text-white">
                  {p.commentCount} comments
                </Link>
              </div>
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function FeedSkeleton() {
  return (
    <ol className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="card p-4 animate-pulse">
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-lg bg-white/5" />
            <div className="flex-1">
              <div className="h-4 bg-white/5 rounded w-3/4 mb-2" />
              <div className="h-3 bg-white/5 rounded w-1/3" />
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="card p-4 border-red-500/30 bg-red-500/5 text-red-300">
      error: {message}
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
